/*
  **Der Tausch in der Listen-Werkzeug-Pille** (#291): Notiz und Memo ↔ die Befehle der Auswahl. Gewählt im
  Prototyp `tools/list-toolbar-selection-motion-prototype`, Variante C „Tausch an Ort und Stelle“ (Sven,
  08.10.2026). Hinter dem Trenner liegt EIN Platz, der in einem Zug breiter oder schmaler wird; was geht,
  schrumpft dort, wo es steht, und blendet aus; was kommt, erscheint gleich an seiner Endstelle und wächst
  ein. Nichts rutscht seitlich — anders als vorher, als die Pille erst breiter und dann wieder schmaler wurde
  (gemessen 91 px).

  **Ohne Notiz und Memo** (Kurz notiert, Pinnwand offen) gibt es nichts zu tauschen; die Auswahl kommt und geht
  trotzdem genauso — derselbe Platz wächst aus dem Nichts, die Teile nacheinander (Sven, 08.10.2026).

  Pinnwand auf/zu und das Ausfahren am Rand behalten ihre Bewegung (`growMotion`, ListToolbar.tsx).

  Reine Rechnung, Sekunden — die Pille spielt sie ab.
*/

/** Notiz/Memo → Auswahl und zurück; `selectionIn`/`selectionOut`: die Auswahl ohne Notiz und Memo. */
export type SwapKind = 'toSelection' | 'toPlacement' | 'selectionIn' | 'selectionOut'

/** Kommt die Auswahl (sonst geht sie)? */
export const selectionComes = (kind: SwapKind) => kind === 'toSelection' || kind === 'selectionIn'
/** Tauscht sie mit Notiz und Memo (sonst kommt oder geht sie allein)? */
export const withPlacement = (kind: SwapKind) => kind === 'toSelection' || kind === 'toPlacement'

/** Die Werte aus dem Prototyp (Werte-Block vom 08.10.2026, nachgeschliffen in Variante F „App jetzt“). */
export const SWAP = {
  /** Der Platz wird in einem Zug breiter oder schmaler. */
  duration: 0.34,
  /** Die Haus-Kurve (wie `heightMotion`). */
  ease: [0.4, 0, 0.2, 1] as const,
  /** Notiz und Memo gehen nacheinander: Memo zuerst, Notiz so viel später. */
  placementStagger: 0.12,
  /** Zwischen den neuen Knöpfen. */
  stagger: 0.06,
  /** Bis der erste neue Knopf kommt. */
  delay: 0.12,
  /** Was geht, schrumpft auf … */
  scaleOut: 0.6,
  /** … was kommt, wächst ab. */
  scaleIn: 0.7,
  /** Anteile der Dauer: Notiz/Memo gehen, Neues kommt, die Auswahl geht beim Zurück. */
  outShare: 0.7,
  inShare: 0.8,
  backShare: 0.7,
  /** Vorsprung der Anzahl vor den Knöpfen, in Versätzen — 0: sie kommt im selben Takt wie die Knöpfe. */
  countLead: 0,
} as const

export interface SwapStep {
  /** `note`/`memo`, oder der Platz eines Befehls der Auswahl (0 = die Anzahl). */
  item: 'note' | 'memo' | number
  from: { opacity: number; scale: number }
  to: { opacity: number; scale: number }
  duration: number
  delay: number
}

const shown = { opacity: 1, scale: 1 }

/**
 * Wer wann wie lange was tut. `selectionItems` zählt die Teile der Auswahl in ihrer Reihenfolge, die Anzahl
 * zuerst; sie kommt `countLead` Versätze vor den Knöpfen (derzeit 0: im selben Takt).
 */
export function swapTimeline(kind: SwapKind, selectionItems: number): SwapStep[] {
  const { duration: T, placementStagger, stagger, delay, scaleOut, scaleIn, outShare, inShare, backShare, countLead } = SWAP
  const steps: SwapStep[] = []
  const selectionDelay = (i: number) => delay + (i > 0 ? stagger * countLead : 0) + i * stagger

  if (selectionComes(kind)) {
    // Von außen nach innen: das Memo zuerst.
    if (withPlacement(kind)) {
      steps.push({ item: 'memo', from: shown, to: { opacity: 0, scale: scaleOut }, duration: T * outShare, delay: 0 })
      steps.push({ item: 'note', from: shown, to: { opacity: 0, scale: scaleOut }, duration: T * outShare, delay: placementStagger })
    }
    for (let i = 0; i < selectionItems; i++) {
      steps.push({ item: i, from: { opacity: 0, scale: scaleIn }, to: shown, duration: T * inShare, delay: selectionDelay(i) })
    }
  } else {
    // Zurück: die Befehle gehen rasch von rechts nach links, Notiz und Memo kommen von innen nach außen.
    for (let i = 0; i < selectionItems; i++) {
      steps.push({ item: i, from: shown, to: { opacity: 0, scale: scaleOut }, duration: T * backShare, delay: (selectionItems - 1 - i) * stagger * 0.5 })
    }
    if (withPlacement(kind)) {
      steps.push({ item: 'note', from: { opacity: 0, scale: scaleIn }, to: shown, duration: T * inShare, delay })
      steps.push({ item: 'memo', from: { opacity: 0, scale: scaleIn }, to: shown, duration: T * inShare, delay: delay + placementStagger })
    }
  }
  return steps
}

/** Wann der ganze Tausch vorbei ist: der Platz und der letzte Knopf. */
export function swapEnd(steps: readonly SwapStep[]): number {
  return Math.max(SWAP.duration, ...steps.map(step => step.delay + step.duration))
}

/** Die meisten Teile der Auswahl: Anzahl, drei Knöpfe, ⋯. */
const MAX_SELECTION_ITEMS = 5

/**
 * **Bis wann das Gehende stehen bleiben muss** — der Platz und das letzte, das ausblendet, in beide Richtungen.
 * Seit der kürzeren Dauer (340 ms) blendet die Notiz erst nach dem Platz ganz aus; ein Segment, das mit dem
 * Platz aus dem DOM ginge, schnitte sie ab.
 */
export const SWAP_LEAVE_END = Math.max(
  SWAP.duration,
  ...(['toSelection', 'toPlacement', 'selectionIn', 'selectionOut'] as const).flatMap(kind =>
    swapTimeline(kind, MAX_SELECTION_ITEMS)
      .filter(step => step.to.opacity === 0)
      .map(step => step.delay + step.duration)),
)
